import React, { useState, useEffect, useCallback, useRef } from "react";
import Sidebar from "../../sidebar/sidebar";
import TopBar from "../../topbar/TopBar";
import { Row, Col, Card } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import searchIcon from "../../../assets/svg/search_gray.svg";
import filterIcon from "../../../assets/svg/filt.svg";
import calendarIcon from "../../../assets/svg/calendar.svg";
import dropDownIcon from "../../../assets/svg/dropdown.svg";
import closeIcon from "../../../assets/svg/close.svg";
import nfcActiveIcon from "../../../assets/svg/nfc_active.svg";
import greenCalenderIcon from "../../../assets/svg/green_calender.svg";
import greenStepIcon from "../../../assets/svg/green_step.svg";
import defaultProfile from "../../../assets/png/user-icon.png";
import CustomPagination from "../../common/CustomPagination";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";
import { Bar } from "react-chartjs-2";
import { getNfcDetails, updateNfcStatus, getNfcHistory, getNfcChartData, deleteNfc } from "./nfc-api";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const dashPlugin = {
  id: "dashPlugin",
  afterDatasetsDraw: (chart) => {
    const activeElements = chart.getActiveElements();
    let index = -1;

    if (activeElements?.[0]) {
      index = activeElements[0].index;
    }

    if (index !== -1 && index !== null) {
      const { ctx } = chart;
      const meta0 = chart.getDatasetMeta(0);
      const meta1 = chart.getDatasetMeta(1);

      if (!ctx || !meta0 || !meta1) return;
      if (!meta0.data[index] || !meta1.data[index]) return;

      const bar0 = meta0.data[index];
      const bar1 = meta1.data[index];

      const { top, bottom } = chart.chartArea;

      const rectLeft = Math.min(bar0.x, bar1.x) - 15;
      const rectRight = Math.max(bar0.x, bar1.x) + 15;
      const rectWidth = rectRight - rectLeft;
      const rectHeight = bottom - top + 20;
      const rectTop = top - 10;

      ctx.save();
      ctx.setLineDash([5, 5]);
      ctx.strokeStyle = "#4F46E5";
      ctx.lineWidth = 2;

      const radius = 8;
      ctx.beginPath();
      ctx.moveTo(rectLeft + radius, rectTop);
      ctx.lineTo(rectLeft + rectWidth - radius, rectTop);
      ctx.quadraticCurveTo(rectLeft + rectWidth, rectTop, rectLeft + rectWidth, rectTop + radius);
      ctx.lineTo(rectLeft + rectWidth, rectTop + rectHeight - radius);
      ctx.quadraticCurveTo(rectLeft + rectWidth, rectTop + rectHeight, rectLeft + rectWidth - radius, rectTop + rectHeight);
      ctx.lineTo(rectLeft + radius, rectTop + rectHeight);
      ctx.quadraticCurveTo(rectLeft, rectTop + rectHeight, rectLeft, rectTop + rectHeight - radius);
      ctx.lineTo(rectLeft, rectTop + radius);
      ctx.quadraticCurveTo(rectLeft, rectTop, rectLeft + radius, rectTop);
      ctx.closePath();
      ctx.stroke();
      ctx.restore();
    }
  },
};

const NfcView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const chartRef = useRef(null);
  const nfcId = location.state?.id;

  // CSS to hide default calendar icons
  const hideCalendarIconStyle = `
    input[type="date"]::-webkit-calendar-picker-indicator,
    input[type="month"]::-webkit-calendar-picker-indicator {
      display: none;
      -webkit-appearance: none;
    }
  `;

  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const statusDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [activeChartTab, setActiveChartTab] = useState("Daily");
  const [chartDate, setChartDate] = useState(new Date().toISOString().split("T")[0]);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [nfcDetails, setNfcDetails] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [chartDataState, setChartDataState] = useState({ labels: [], datasets: [] });
  const [totalCount, setTotalCount] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySearch, setHistorySearch] = useState("");
  const [activeHistoryType, setActiveHistoryType] = useState(""); // Visitor/Vehicle
  const [historyDate, setHistoryDate] = useState(new Date().toISOString().split("T")[0]);

  const fetchDetails = useCallback(async () => {
    if (!nfcId) return;
    setDetailsLoading(true);
    const response = await getNfcDetails(nfcId);
    if (response?.data?.success) {
      setNfcDetails(response.data.data);
    }
    setDetailsLoading(false);
  }, [nfcId]);

  const fetchHistory = useCallback(async () => {
    if (!nfcId) return;
    setHistoryLoading(true);
    const params = {
      limit: itemsPerPage,
      skip: currentPage * itemsPerPage,
      search: historySearch,
      type: activeHistoryType || undefined,
      date: historyDate || undefined,
    };
    const response = await getNfcHistory(nfcId, params);
    if (response?.data?.success) {
      setHistoryData(response.data.data || []);
      setTotalCount(response.data.total_recs || 0);
      setPageCount(response.data.total_pgs || 0);
    }
    setHistoryLoading(false);
  }, [nfcId, itemsPerPage, currentPage, historySearch, activeHistoryType, historyDate]);

  const fetchChart = useCallback(async () => {
    if (!nfcId) return;
    setChartLoading(true);
    const typeMap = { Daily: "daily", Monthly: "monthly", Yearly: "yearly" };
    const response = await getNfcChartData(nfcId, { type: typeMap[activeChartTab], date: chartDate || undefined });
    if (response?.data?.success) {
      const apiData = response.data.data || [];
      setChartDataState({
        labels: apiData.map((d) => d.date),
        datasets: [
          {
            label: "Visitor",
            data: apiData.map((d) => d.Visitor),
            backgroundColor: "#97A9FF",
            hoverBackgroundColor: "#3B5DFF",
            borderRadius: 4,
            barThickness: 6,
          },
          {
            label: "Vehicle",
            data: apiData.map((d) => d.Vehicle),
            backgroundColor: "#FFD86B",
            hoverBackgroundColor: "#F0B000",
            borderRadius: 4,
            barThickness: 6,
          },
        ],
      });
    }
    setChartLoading(false);
  }, [nfcId, activeChartTab, chartDate]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  useEffect(() => {
    fetchChart();
  }, [fetchChart]);

  const handleStatusUpdate = async (newStatus) => {
    if (!nfcId) return;
    const response = await updateNfcStatus({ id: nfcId, status: newStatus });
    if (response?.data?.success) {
      fetchDetails();
    }
  };

  const handleDelete = async () => {
    if (!nfcId) return;
    if (window.confirm("Are you sure you want to delete this NFC tag?")) {
      const response = await deleteNfc(nfcId);
      if (response?.data?.success) {
        navigate("/nfc");
      }
    }
  };

  const handlePageClick = (data) => {
    setCurrentPage(data.selected);
  };

  const decodeSeqNo = (encoded) => {
    if (!encoded) return "-";
    try {
      return atob(encoded);
    } catch (e) {
      return encoded;
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: "index",
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: "rgba(255, 255, 255, 0.98)",
        titleColor: "#4F46E5",
        bodyColor: "#5E6C84",
        borderColor: "rgba(0, 0, 0, 0.05)",
        borderWidth: 1,
        padding: 16,
        cornerRadius: 12,
        titleFont: { size: 16, weight: "700", family: "Epilogue" },
        bodyFont: { size: 14, family: "Epilogue", weight: "500" },
        usePointStyle: true,
        boxWidth: 10,
        boxHeight: 10,
        bodySpacing: 12,
        titleMarginBottom: 12,
        callbacks: {
          title: (tooltipItems) => {
            const label = tooltipItems[0].label;
            const shortenMonth = (s) => {
              const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
              let res = s;
              months.forEach((m) => {
                if (res.includes(m)) res = res.replace(m, m.substring(0, 3));
              });
              return res;
            };

            if (activeChartTab === "Monthly") {
              return `${shortenMonth(label)}, ${chartDate.substring(0, 4)}`;
            } else if (activeChartTab === "Yearly") {
              return label;
            } else if (activeChartTab === "Daily") {
              const fullFormatted = label.includes(",") ? label : `${label}, ${chartDate.substring(0, 4)}`;
              return shortenMonth(fullFormatted);
            }
            return shortenMonth(label);
          },
          label: (context) => {
            const label = context.dataset.label || "";
            const value = context.parsed.y || 0;
            return ` ${label}            ${value}`;
          },
        },
        yAlign: "center",
        xAlign: "left",
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: "#8790A1", font: { family: "Epilogue", size: 12 } },
      },
      y: {
        beginAtZero: true,
        grid: { borderDash: [5, 5], color: "rgba(235, 237, 240, 1)", drawBorder: false },
        ticks: { color: "#8790A1", font: { family: "Epilogue", size: 12 } },
      },
    },
  };

  if (!nfcDetails && !detailsLoading)
    return (
      <div style={{ padding: "100px", textAlign: "center" }}>
        No details found. <button onClick={() => navigate("/nfc")}>Go Back</button>
      </div>
    );

  return (
    <div style={{ fontFamily: "Epilogue", backgroundColor: "#FFFFFF", minHeight: "100vh" }}>
      <style>{hideCalendarIconStyle}</style>
      <TopBar />
      <div style={{ display: "flex" }}>
        <Sidebar activeMenu={"NFC"} />
        <div style={{ flex: 1, padding: "24px", minHeight: "calc(100vh - 64px)" }}>
          <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "32px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                <div style={{ backgroundColor: "#E6FAF0", width: "56px", height: "56px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <img src={nfcActiveIcon} alt="nfc" style={{ width: "28px", filter: "invert(40%) sepia(85%) saturate(1814%) hue-rotate(124deg) brightness(98%) contrast(97%)" }} />
                </div>
                <h1 style={{ fontSize: "24px", fontWeight: "800", color: "#172B4D", margin: 0 }}>NFC#{decodeSeqNo(nfcDetails?.seq_no)}</h1>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                <div style={{ position: "relative" }} ref={statusDropdownRef}>
                  <div
                    onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      borderRadius: "16px",
                      padding: "10px 24px",
                      backgroundColor: "#fff",
                      border: "1px solid #E5E5E5",
                      cursor: "pointer",
                      boxShadow: "0px 2px 4px rgba(0,0,0,0.02)",
                    }}
                  >
                    <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: nfcDetails?.status ? "#2C8C53" : "#D32F2F" }} />
                    <span style={{ fontSize: "15px", fontWeight: "600", color: "#172B4D" }}>{nfcDetails?.status ? "Active" : "Inactive"}</span>
                    <img src={dropDownIcon} alt="dropdown" style={{ width: "12px", opacity: 0.7, transform: showStatusDropdown ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
                  </div>

                  {showStatusDropdown && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        marginTop: "8px",
                        width: "100%",
                        backgroundColor: "#FFFFFF",
                        borderRadius: "12px",
                        border: "1px solid #E5E5E5",
                        boxShadow: "0px 8px 16px rgba(0,0,0,0.08)",
                        zIndex: 10,
                        overflow: "hidden",
                      }}
                    >
                      {[
                        { label: "Active", value: true, color: "#2C8C53" },
                        { label: "Inactive", value: false, color: "#D32F2F" },
                      ].map((opt) => (
                        <div
                          key={opt.label}
                          onClick={() => {
                            handleStatusUpdate(opt.value);
                            setShowStatusDropdown(false);
                          }}
                          style={{
                            padding: "12px 24px",
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            cursor: "pointer",
                            fontSize: "14px",
                            fontWeight: "500",
                            color: "#172B4D",
                            backgroundColor: nfcDetails?.status === opt.value ? "#F3F4F6" : "transparent",
                          }}
                        >
                          <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: opt.color }} />
                          {opt.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div
                  onClick={() => navigate("/nfc")}
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    backgroundColor: "#F7F8FA",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    border: "1px solid #F0F2F5",
                  }}
                >
                  <img src={closeIcon} alt="close" style={{ width: "14px" }} />
                </div>
              </div>
            </div>

            <Row className="g-4 mb-4">
              {/* Last allocated status */}
              <Col lg={4}>
                <Card style={{ borderRadius: "24px", border: "none", padding: "16px", gap: "8px", backgroundColor: "#F8F8F8" }}>
                  <h2 style={{ fontSize: "18px", fontWeight: "600", color: "#172B4D", marginBottom: "20px" }}>Current status</h2>
                  {detailsLoading ? (
                    <div style={{ textAlign: "center", padding: "40px", color: "#8790A1" }}>Loading details...</div>
                  ) : (
                    <>
                      <div style={{ backgroundColor: "#fff", border: "1.5px solid #F0F2F5", borderRadius: "16px", padding: "16px", marginBottom: "16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                          <img
                            src={(nfcDetails?.assign_type === "Visitor" ? nfcDetails?.visitor_profile : nfcDetails?.driver_profile) || defaultProfile}
                            alt="profile"
                            style={{ width: "48px", height: "48px", borderRadius: "50%", objectFit: "cover" }}
                            onError={(e) => {
                              e.target.src = defaultProfile;
                            }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                              <h3 style={{ fontSize: "14px", fontWeight: "700", color: "#172B4D", margin: 0 }}>{nfcDetails?.person_name ?? "-"}</h3>
                              <span style={{ fontSize: "11px", fontWeight: "500", color: "#8790A1", backgroundColor: "#F4F5F7", padding: "2px 8px", borderRadius: "4px", fontStyle: "italic" }}>{nfcDetails?.assign_type ?? "-"}</span>
                            </div>
                            <p style={{ fontSize: "12px", color: "#8790A1", margin: 0 }}>{nfcDetails?.mobile_number ?? "-"}</p>
                          </div>
                        </div>
                        <div style={{ backgroundColor: "#E8F5E9", padding: "8px 16px", borderRadius: "8px", fontSize: "13px", fontWeight: "500", color: "#172B4D" }}>
                          Vehicle No : <span style={{ fontWeight: "600" }}>{nfcDetails?.vehicle_number ?? "-"}</span>
                        </div>
                      </div>

                      <div style={{ marginBottom: "0px" }}>
                        <label style={{ fontSize: "12px", color: "#8790A1", display: "block" }}>Gate</label>
                        <span style={{ fontSize: "14px", fontWeight: "600", color: "#172B4D" }}>{nfcDetails?.gate_name ?? "-"}</span>
                      </div>

                      <hr style={{ border: "0.5px solid #E5E5E5", margin: "16px 0" }} />

                      <div>
                        <label style={{ fontSize: "12px", color: "#8790A1", display: "block", marginBottom: "4px" }}>Last updated on</label>
                        <span style={{ fontSize: "14px", fontWeight: "600", color: "#172B4D" }}>
                          {nfcDetails?.update_at || "-"} by {nfcDetails?.updated_by || "-"}
                        </span>
                      </div>
                    </>
                  )}
                </Card>
              </Col>

              {/* Analytics */}
              <Col lg={8}>
                <Card style={{ borderRadius: "24px", border: "1px solid #E5E5E5", padding: "16px", height: "100%" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", marginBottom: "24px" }}>
                    <h2 style={{ fontSize: "18px", fontWeight: "700", color: "#172B4D", margin: 0 }}>Analytics</h2>

                    <div style={{ height: "40px", backgroundColor: "#F7F8FA", padding: "10px 6px", borderRadius: "30px", display: "flex", gap: "24px", border: "1px solid #F0F2F5", alignItems: "center", justifyContent: "center" }}>
                      {["Daily", "Monthly", "Yearly"].map((tab) => (
                        <button
                          key={tab}
                          onClick={() => setActiveChartTab(tab)}
                          style={{
                            outline: "none",
                            padding: "4px 24px 0 24px",
                            borderRadius: "100px",
                            fontSize: "14px",
                            fontWeight: "600",
                            height: "32px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: activeChartTab === tab ? "1px solid #E5E5E5" : "none",
                            backgroundColor: activeChartTab === tab ? "#fff" : "transparent",
                            color: activeChartTab === tab ? "#3469E7" : "#8790A1",
                            boxShadow: activeChartTab === tab ? "0px 4px 10px rgba(0,0,0,0.05)" : "none",
                            transition: "all 0.2s ease",
                          }}
                        >
                          {tab}
                        </button>
                      ))}
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "4px", cursor: "pointer", justifySelf: "end", backgroundColor: "#fff", border: "none", borderRadius: "12px", padding: "4px 8px" }}>
                      {activeChartTab === "Daily" ? (
                        <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                          <span style={{ fontSize: "14px", fontWeight: "600", color: "#4F46E5", fontFamily: "Epilogue", cursor: "pointer", pointerEvents: "none" }}>
                            {(() => {
                              const [y, m] = chartDate.substring(0, 7).split("-");
                              const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                              return `${months[parseInt(m) - 1]}, ${y}`;
                            })()}
                          </span>
                          <input
                            type="month"
                            value={chartDate.substring(0, 7)}
                            onChange={(e) => setChartDate(e.target.value + "-01")}
                            onClick={(e) => e.target.showPicker()}
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              opacity: 0,
                              border: "none",
                              outline: "none",
                              cursor: "pointer",
                            }}
                          />
                        </div>
                      ) : (
                        <select value={chartDate.substring(0, 4)} onChange={(e) => setChartDate(e.target.value + "-01-01")} style={{ border: "none", outline: "none", backgroundColor: "transparent", fontSize: "14px", fontWeight: "600", color: "#4F46E5", fontFamily: "Epilogue", cursor: "pointer", appearance: "none", width: "100px", textAlign: "right" }}>
                          {[2023, 2024, 2025, 2026, 2027, 2028, 2029, 2030].map((y) => (
                            <option key={y} value={y}>
                              {y}
                            </option>
                          ))}
                        </select>
                      )}
                      <img src={dropDownIcon} alt="dropdown" style={{ width: "10px", filter: "invert(24%) sepia(91%) saturate(3475%) hue-rotate(232deg) brightness(91%) contrast(101%)" }} />
                    </div>
                  </div>
                  <div style={{ height: "300px", position: "relative" }}>
                    {chartLoading ? (
                      <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(255,255,255,0.7)", zIndex: 1 }}>
                        <span style={{ fontSize: "14px", fontWeight: "600", color: "#8790A1" }}>Updating chart...</span>
                      </div>
                    ) : null}
                    <Bar ref={chartRef} data={chartDataState} options={chartOptions} plugins={[dashPlugin]} />
                  </div>
                </Card>
              </Col>
            </Row>

            {/* History Section */}
            <Card style={{ borderRadius: "20px", border: "none", padding: "24px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
                <h2 style={{ fontSize: "20px", fontWeight: "700", color: "#172B4D", margin: 0 }}>
                  History <span style={{ color: "#8790A1", fontWeight: "500", marginLeft: "4px" }}>({historyLoading ? "..." : historyData.length > 0 ? pageCount * itemsPerPage : 0})</span>
                </h2>

                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      backgroundColor: "#fff",
                      borderRadius: "12px",
                      padding: "0 16px",
                      border: "1px solid #E5E5E5",
                      width: "320px",
                      height: "36px",
                    }}
                  >
                    <img src={searchIcon} alt="search" style={{ width: "22px" }} />
                    <input
                      type="text"
                      placeholder="Search"
                      value={historySearch}
                      onChange={(e) => setHistorySearch(e.target.value)}
                      style={{ border: "none", outline: "none", backgroundColor: "transparent", fontSize: "14px", width: "100%", height: "100%", color: "#172B4D" }}
                    />
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      backgroundColor: "#fff",
                      border: "1px solid #E5E5E5",
                      borderRadius: "12px",
                      padding: "0 16px",
                      height: "36px",
                      cursor: "pointer",
                    }}
                  >
                    <img src={greenCalenderIcon} alt="calendar" style={{ width: "16px" }} />
                    <input type="date" value={historyDate} onChange={(e) => setHistoryDate(e.target.value)} onClick={(e) => e.target.showPicker()} style={{ border: "none", outline: "none", fontSize: "14px", fontWeight: "600", color: "#2C8C53", backgroundColor: "transparent", cursor: "pointer", height: "100%" }} />
                  </div>

                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      backgroundColor: "#fff",
                      border: "1px solid #E5E5E5",
                      borderRadius: "12px",
                      padding: "0 16px",
                      height: "36px",
                    }}
                  >
                    <img src={greenStepIcon} alt="step" style={{ width: "16px" }} />
                    <select
                      value={activeHistoryType}
                      onChange={(e) => setActiveHistoryType(e.target.value)}
                      style={{
                        appearance: "none",
                        backgroundColor: "#fff",
                        border: "none",
                        padding: "0 24px 0 0",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#2C8C53",
                        cursor: "pointer",
                        outline: "none",
                        height: "100%",
                      }}
                    >
                      <option value="">All Entry types</option>
                      <option value="Visitor">Visitor</option>
                      <option value="Vehicle">Vehicle</option>
                    </select>
                    <img src={dropDownIcon} alt="dropdown" style={{ position: "absolute", right: "16px", top: "50%", transform: "translateY(-50%)", width: "12px", pointerEvents: "none", filter: "invert(43%) sepia(34%) saturate(913%) hue-rotate(95deg) brightness(97%) contrast(89%)" }} />
                  </div>
                </div>
              </div>

              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#F9FAFB", textAlign: "left", borderBottom: "1px solid #F0F2F5" }}>
                      <th style={headerStyle}>S.No</th>
                      <th style={headerStyle}>Person Info</th>
                      <th style={headerStyle}>Vehicle No.</th>
                      <th style={headerStyle}>Gate</th>
                      <th style={headerStyle}>Assigned For</th>
                      <th style={headerStyle}>Created At</th>
                      <th style={headerStyle}>Last Updated On</th>
                      <th style={headerStyle}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyLoading ? (
                      <tr>
                        <td colSpan="8" style={{ textAlign: "center", padding: "40px", color: "#8790A1" }}>
                          Loading history...
                        </td>
                      </tr>
                    ) : historyData.length === 0 ? (
                      <tr>
                        <td colSpan="8" style={{ textAlign: "center", padding: "40px", color: "#8790A1" }}>
                          No history found
                        </td>
                      </tr>
                    ) : (
                      historyData.map((item, index) => (
                        <tr key={index} style={{ borderBottom: "1px solid #F0F2F5" }}>
                          <td style={cellStyle}>{currentPage * itemsPerPage + index + 1}</td>
                          <td style={cellStyle}>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                              <img
                                src={(item.assign_type === "Visitor" ? item.visitor_profile : item.driver_profile) || defaultProfile}
                                alt="avatar"
                                style={{ width: "32px", height: "32px", borderRadius: "50%", objectFit: "cover" }}
                                onError={(e) => {
                                  e.target.src = defaultProfile;
                                }}
                              />
                              <div style={{ display: "flex", flexDirection: "column" }}>
                                <span style={{ fontWeight: "600", color: "#172B4D", fontSize: "13px" }}>{item.person_name}</span>
                                <span style={{ color: "#8790A1", fontSize: "11px" }}>{item.mobile_number}</span>
                              </div>
                            </div>
                          </td>
                          <td style={cellStyle}>{item.vehicle_number || "-"}</td>
                          <td style={cellStyle}>{item.gate_name || "-"}</td>
                          <td style={cellStyle}>
                            <span style={{ backgroundColor: "#F4F5F7", padding: "4px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "600", color: "#5E6C84" }}>{item.assign_type}</span>
                          </td>
                          <td style={cellStyle}>{item.created_at || "-"}</td>
                          <td style={cellStyle}>{item.update_at || "-"}</td>
                          <td style={cellStyle}>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <div style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: item.status ? "#2C8C53" : "#FF0000" }} />
                              <span style={{ fontSize: "12px", fontWeight: "500", color: "#172B4D" }}>{item.status ? "Active" : "Inactive"}</span>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {historyData.length > 0 && (
                <div style={{ marginTop: "24px" }}>
                  <CustomPagination itemsPerPage={itemsPerPage} setItemsPerPage={setItemsPerPage} pageCount={pageCount} onPageChange={handlePageClick} currentPage={currentPage} />
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

const headerStyle = {
  padding: "12px 16px",
  fontSize: "13px",
  fontWeight: "600",
  color: "#172B4D",
  border: "1px solid #E5E5E5",
  backgroundColor: "#F9FAFB",
};

const cellStyle = {
  padding: "12px 16px",
  fontSize: "13px",
  fontWeight: "500",
  color: "#172B4D",
  verticalAlign: "middle",
  border: "1px solid #E5E5E5",
};

export default NfcView;
